<template>
  <div
    class="container-fluid body auth-page-skin"
    :dir="$i18n.locale === 'fa' || $i18n.locale === 'ar' ? 'rtl' : 'ltr'"
  >

    <Navbar />

    <div class="login-container">
      <div class="login-card">

        <h2>{{ $t('otp.verifyPhone') }}</h2>
        <p class="subtitle">{{ $t('otp.verifyPhoneMessage') }}</p>

        <!-- Step 1: Phone -->
        <div v-if="step === 1" class="phone-step">
          <div class="form-group">
            <label class="form-label">{{ $t('otp.phoneNumber') }}</label>

            <input
              v-model="phoneNumber"
              class="form-input"
              :placeholder="$t('otp.phoneNumberPlaceholder')"
              disabled
            />

            <p class="phone-note">{{ $t('otp.registeredPhoneNote') }}</p>
          </div>

          <button class="btn-primary" @click="sendOTP" :disabled="sendingOTP">
            {{ sendingOTP ? $t('otp.sending') : $t('otp.sendOTP') }}
          </button>

          <button class="btn-secondary mt-3" @click="goBack">
            {{ $t('otp.back') }}
          </button>
        </div>

        <!-- Step 2: OTP -->
        <div v-if="step === 2" class="otp-step">
          <div class="form-group">
            <label class="form-label">{{ $t('otp.enterOTP') }}</label>

            <input
              v-model="otpCode"
              class="form-input"
              :placeholder="$t('otp.otpPlaceholder')"
              maxlength="6"
            />

            <p class="otp-note">{{ $t('otp.otpSentToPhone') }}</p>
          </div>

          <div v-if="errorMessage" class="alert alert-danger">
            {{ errorMessage }}
          </div>

          <div v-if="successMessage" class="alert alert-success">
            {{ successMessage }}
          </div>

          <button
            class="btn-primary"
            @click="verifyAndRegister"
            :disabled="verifying || otpCode.length !== 6"
          >
            {{ verifying ? $t('otp.verifying') : $t('otp.verifyAndRegister') }}
          </button>

          <div class="resend-section">
            <p>{{ $t('otp.notReceivedOTP') }}</p>

            <button class="btn-link" @click="resendOTP" :disabled="resendCooldown > 0">
              {{ $t('otp.resendOTP') }}
              <span v-if="resendCooldown > 0">({{ resendCooldown }})</span>
            </button>
          </div>

          <button class="btn-secondary mt-3" @click="step = 1">
            {{ $t('otp.back') }}
          </button>
        </div>

      </div>
    </div>

  </div>
</template>

<script>
import { authService, registerService } from '@/services/api'
import { useAuthStore } from '@/stores/auth'
import Navbar from '@/components/navbar_styled_register.vue';

export default {
  name: 'RegisterCompanyPhone',
  props: {
    company: {
      type: Object,
      required: true
    }
  },
  components: { Navbar },
  data() {
    return {
      currentLang: 'fa',
      step: 1,
      phoneNumber: '',
      otpCode: '',
      sendingOTP: false,
      verifying: false,
      errorMessage: '',
      successMessage: '',
      resendCooldown: 0,
      resendTimer: null,
      
      translations: {
        en: {
          verifyPhone: "Verify Phone Number",
          verifyPhoneMessage: "To register with this company, please verify your phone number",
          phoneNumber: "Phone Number",
          phoneNumberPlaceholder: "Enter your phone number",
          registeredPhoneNote: "This is your registered phone number",
          sendOTP: "Send Verification Code",
          sending: "Sending...",
          enterOTP: "Enter Verification Code",
          otpPlaceholder: "Enter 6-digit code",
          otpSentToPhone: "Verification code sent to your phone",
          verifyAndRegister: "Verify & Register",
          verifying: "Verifying...",
          notReceivedOTP: "Didn't receive the code?",
          resendOTP: "Resend Code",
          back: "Back",
          companyRegistrationSuccess: "Successfully registered with company!",
          otpVerified: "OTP verified successfully"
        },
        fa: {
          verifyPhone: "تایید شماره موبایل",
          verifyPhoneMessage: "برای ثبت‌نام در این شرکت، لطفا شماره موبایل خود را تایید کنید",
          phoneNumber: "شماره موبایل",
          phoneNumberPlaceholder: "شماره موبایل خود را وارد کنید",
          registeredPhoneNote: "این شماره موبایل ثبت‌شده شماست",
          sendOTP: "ارسال کد تایید",
          sending: "در حال ارسال...",
          enterOTP: "کد تایید را وارد کنید",
          otpPlaceholder: "کد ۶ رقمی را وارد کنید",
          otpSentToPhone: "کد تایید به شماره شما ارسال شد",
          verifyAndRegister: "تایید و ثبت‌نام",
          verifying: "در حال تایید...",
          notReceivedOTP: "کد را دریافت نکردید؟",
          resendOTP: "ارسال مجدد کد",
          back: "بازگشت",
          companyRegistrationSuccess: "با موفقیت در شرکت ثبت‌نام شدید!",
          otpVerified: "کد تایید با موفقیت تایید شد"
        },
        ar: {
          verifyPhone: "تحقق من رقم الهاتف",
          verifyPhoneMessage: "للتسجيل في هذه الشركة ، يرجى التحقق من رقم هاتفك",
          phoneNumber: "رقم الهاتف",
          phoneNumberPlaceholder: "أدخل رقم هاتفك",
          registeredPhoneNote: "هذا هو رقم هاتفك المسجل",
          sendOTP: "إرسال رمز التحقق",
          sending: "جاري الإرسال...",
          enterOTP: "أدخل رمز التحقق",
          otpPlaceholder: "أدخل الرمز المكون من 6 أرقام",
          otpSentToPhone: "تم إرسال رمز التحقق إلى هاتفك",
          verifyAndRegister: "التحقق والتسجيل",
          verifying: "جاري التحقق...",
          notReceivedOTP: "لم تستلم الرمز؟",
          resendOTP: "إعادة إرسال الرمز",
          back: "رجوع",
          companyRegistrationSuccess: "تم التسجيل في الشركة بنجاح!",
          otpVerified: "تم التحقق من رمز التحقق بنجاح"
        }
      }
    }
  },
  mounted() {
    const savedLang = localStorage.getItem('selectedLanguage')
    if (savedLang) {
      this.currentLang = savedLang
    }
    this.loadUserPhone()
  },
  beforeUnmount() {
    if (this.resendTimer) {
      clearInterval(this.resendTimer)
    }
  },
  methods: {
    switchLanguage() {
      localStorage.setItem('selectedLanguage', this.currentLang)
    },
    
    loadUserPhone() {
      const authStore = useAuthStore()
      if (authStore.user && authStore.user.phone) {
        this.phoneNumber = authStore.user.phone
      } else {
        // Try to get from localStorage as fallback
        const userStr = localStorage.getItem('user')
        if (userStr) {
          try {
            const user = JSON.parse(userStr)
            this.phoneNumber = user.phone || ''
          } catch (e) {
            console.error('Error parsing user from localStorage:', e)
          }
        }
      }
    },
    
    async sendOTP() {
      if (!this.phoneNumber) {
        this.errorMessage = this.translations[this.currentLang].phoneNumberRequired
        return
      }
      
      this.sendingOTP = true
      this.errorMessage = ''
      
      try {
        // Call send-otp endpoint
        const response = await registerService.register(this.phoneNumber)
        
        if (response.data.success) {
          this.step = 2
          this.startResendCooldown()
          this.successMessage = this.translations[this.currentLang].otpSentToPhone
        } else {
          this.errorMessage = response.data.message || 'Failed to send OTP'
        }
      } catch (error) {
        console.error('Error sending OTP:', error)
        this.handleError(error)
      } finally {
        this.sendingOTP = false
      }
    },
    
    async verifyAndRegister() {
      if (!this.otpCode || this.otpCode.length !== 6) {
        this.errorMessage = this.translations[this.currentLang].enterValidOTP
        return
      }
      
      this.verifying = true
      this.errorMessage = ''
      this.successMessage = ''
      
      try {
          const registerResponse = await authService.registerCompany(
            this.company.company_id,
            this.otpCode
          )
          
          if (registerResponse.data.success) {
            this.successMessage = this.translations[this.currentLang].companyRegistrationSuccess
            
            // Update auth store with new authentication status
            const authStore = useAuthStore()
            
            // Update company authentication status
            if (authStore.companies) {
              const updatedCompanies = authStore.companies.map(company => {
                if (company.company_id === this.company.company_id) {
                  return {
                    ...company,
                    is_authenticated: true // Add this flag
                  }
                }
                return company
              })
              
              authStore.companies = updatedCompanies
              localStorage.setItem('companies', JSON.stringify(updatedCompanies))
            }
            
            // Wait a moment and redirect back to login
            setTimeout(() => {
              this.$router.push('/login')
            }, 2000)
          } else {
            this.errorMessage = registerResponse.data.message || 'Failed to register with company'
          }
      } catch (error) {
        console.error('Error during verification/registration:', error)
        this.handleError(error)
      } finally {
        this.verifying = false
      }
    },
    
    async resendOTP() {
      if (this.resendCooldown > 0) return
      
      await this.sendOTP()
    },
    
    startResendCooldown() {
      this.resendCooldown = 60
      this.resendTimer = setInterval(() => {
        this.resendCooldown--
        if (this.resendCooldown <= 0) {
          clearInterval(this.resendTimer)
          this.resendTimer = null
        }
      }, 1000)
    },
    
    goBack() {
      this.$router.go(-1)
    },
    
    handleError(error) {
      if (error.response) {
        const { data, status } = error.response
        if (status === 400) {
          this.errorMessage = data.detail || data.message || 'Invalid request'
        } else if (status === 401) {
          this.errorMessage = 'Session expired. Please login again.'
        } else if (status === 404) {
          this.errorMessage = 'Phone number not found'
        } else {
          this.errorMessage = data.detail || data.message || 'Server error'
        }
      } else if (error.request) {
        this.errorMessage = 'Network error. Please check your connection.'
      } else {
        this.errorMessage = error.message || 'Unknown error occurred'
      }
    }
  }
}
</script>

<style>
.login-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  padding: 1rem;
  background: var(--bg);
}

.login-card {
  background: var(--card-bg);
  color: var(--text);
  padding: 2rem;
  border-radius: 12px;
  border: 1px solid var(--border);
  box-shadow: var(--shadow);
  text-align: center;
  width: 100%;
  max-width: 400px;
}

.subtitle {
  color: var(--text-secondary);
  margin-bottom: 2rem;
}

.form-group {
  margin-bottom: 1.5rem;
  text-align: inherit;
}

.form-label {
  margin-bottom: 0.5rem;
  font-weight: 500;
}

.form-input {
  width: 100%;
  padding: 0.75rem;
  background: var(--input-bg);
  border: 1px solid var(--border);
  border-radius: 6px;
  font-size: 1rem;
  color: var(--text);
}

.phone-note,
.otp-note {
  font-size: 0.875rem;
  color: var(--text-secondary);
  margin-top: 0.5rem;
}

.btn-primary,
.btn-secondary,
.btn-link {
  width: 100%;
  padding: 0.75rem;
  border-radius: 6px;
  cursor: pointer;
  margin-top: 0.5rem;
  border: none;
}

.btn-primary {
  background: var(--primary);
  color: white;
}

.btn-primary:disabled {
  background: var(--disabled);
}

.btn-secondary {
  background: var(--secondary);
  color: white;
}

.btn-link {
  background: none;
  color: var(--primary);
  text-decoration: underline;
}

.alert {
  padding: 0.75rem;
  border-radius: 6px;
  margin-bottom: 1rem;
}

.alert-danger {
  background: #f8d7da;
  color: #721c24;
}

.alert-success {
  background: #d4edda;
  color: #155724;
}

.language-switcher {
  position: fixed;
  top: 1rem;
  right: 1rem;
}

.language-dropdown-select {
  padding: 0.5rem;
  border-radius: 6px;
  border: 1px solid var(--border);
  background: var(--input-bg);
  color: var(--text);
}

/* Shared Register-style auth UI */
.auth-page-skin { --auth-text:#f1f7f5; --auth-muted:#9bb5b4; --auth-surface:rgba(12,42,51,.72); --auth-border:rgba(145,205,194,.2); --auth-input:rgba(5,24,31,.72); --auth-accent:#f07832; --auth-accent-soft:#ff9b5f; position:relative; isolation:isolate; min-height:calc(100svh - 64px); overflow:hidden; padding:56px 20px 72px; color:var(--auth-text); background:radial-gradient(circle at 72% 45%,rgba(35,110,118,.25),transparent 34%),linear-gradient(135deg,#06151e 0%,#08242e 52%,#071b25 100%); font-family:'Vazirmatn',Tahoma,'Segoe UI',sans-serif; }
.auth-page-skin::before { content:''; position:absolute; z-index:-1; inset:-12%; background:url('~@/assets/register-login-bg.svg') center / min(720px,92vw) auto no-repeat; opacity:.34; pointer-events:none; }
.auth-page-skin::after { content:''; position:absolute; z-index:-1; inset:20%; border-radius:50%; background:rgba(240,120,50,.12); filter:blur(70px); pointer-events:none; }
.auth-page-skin > * { position:relative; z-index:1; }
.auth-page-skin .auth-panel { position:relative; z-index:2; width:min(100%,440px); margin:0 auto; padding:36px 32px 30px; border:1px solid var(--auth-border); border-radius:22px; background:var(--auth-surface); color:var(--auth-text); box-shadow:0 24px 70px rgba(0,0,0,.28),inset 0 1px 0 rgba(255,255,255,.04); backdrop-filter:blur(14px); }
.auth-page-skin .auth-panel h2 { color:var(--auth-text); font-size:clamp(22px,3vw,29px); font-weight:800; }
.auth-page-skin .subtitle { color:var(--auth-muted); line-height:1.8; }
.auth-page-skin .form-group { margin-bottom:18px; }
.auth-page-skin .form-label { color:var(--auth-muted); font-size:13px; font-weight:600; }
.auth-page-skin .form-input, .auth-page-skin .otp-input { width:100%; min-height:46px; padding:11px 14px; border:1px solid rgba(145,205,194,.25); border-radius:10px; outline:none; background:var(--auth-input); color:var(--auth-text); font:inherit; transition:border-color .2s,box-shadow .2s,background .2s; }
.auth-page-skin .form-input::placeholder { color:#76918f; }
.auth-page-skin .form-input:focus, .auth-page-skin .otp-input:focus { border-color:var(--auth-accent); box-shadow:0 0 0 3px rgba(240,120,50,.16); background:rgba(5,24,31,.88); }
.auth-page-skin .form-input.input-error { border-color:#e07070; }
.auth-page-skin .btn-primary, .auth-page-skin .verify-btn, .auth-page-skin .login-btn, .auth-page-skin .reset-btn, .auth-page-skin .password-btn, .auth-page-skin .submit-btn { min-height:46px; border:0; border-radius:10px; background:var(--auth-accent); color:#fff; font:inherit; font-weight:700; cursor:pointer; box-shadow:0 8px 24px rgba(240,120,50,.2); transition:transform .18s,background .18s,opacity .18s,box-shadow .18s; }
.auth-page-skin .btn-primary:hover:not(:disabled), .auth-page-skin .verify-btn:hover:not(:disabled), .auth-page-skin .login-btn:hover:not(:disabled), .auth-page-skin .reset-btn:hover:not(:disabled), .auth-page-skin .password-btn:hover:not(:disabled), .auth-page-skin .submit-btn:hover:not(:disabled) { transform:translateY(-2px); background:var(--auth-accent-soft); box-shadow:0 12px 32px rgba(240,120,50,.3); }
.auth-page-skin button:disabled { opacity:.65; cursor:wait; }
.auth-page-skin .text-danger, .auth-page-skin .error-message { color:#f28a8a; }
.auth-page-skin .text-success, .auth-page-skin .success-message { color:#7ed6a2; }
.auth-page-skin .error-message, .auth-page-skin .success-message { background:rgba(5,24,31,.48); border-color:var(--auth-border); }
.auth-page-skin .btn-secondary { background:#31545b; color:#fff; border:0; }
.auth-page-skin .btn-link, .auth-page-skin a { color:var(--auth-accent-soft); }
.auth-page-skin .role-selection-content, .auth-page-skin .company-selection-content { background:#0d2b35; color:var(--auth-text); border:1px solid var(--auth-border); }
.auth-page-skin .company-option { border-color:var(--auth-border); background:rgba(5,24,31,.38); }
@media (max-width:767px) { .auth-page-skin { min-height:calc(100svh - 56px); padding:34px 14px 40px; } .auth-page-skin::before { background-size:min(560px,125vw) auto; opacity:.28; } .auth-page-skin .auth-panel { width:min(100%,440px); padding:28px 22px; } }


/* Match Register composition: visual left, auth panel right; visual behind panel on mobile. */
.otp-container.auth-page-skin, .verify-container.auth-page-skin, .password-container.auth-page-skin, .reset-container.auth-page-skin {
  display: grid;
  grid-template-columns: minmax(0, 1.08fr) minmax(320px, .92fr);
  align-items: center;
  gap: clamp(24px, 4vw, 72px);
  width: 100%;
  min-height: calc(100svh - 64px);
  padding: 72px clamp(24px, 5vw, 88px) 32px;
}
.otp-container.auth-page-skin::before, .verify-container.auth-page-skin::before, .password-container.auth-page-skin::before, .reset-container.auth-page-skin::before {
  inset: 8% 42% 4% 2%;
  background-size: min(680px, 56vw) auto;
  background-position: center;
  opacity: .62;
}
.otp-container.auth-page-skin > .auth-panel, .verify-container.auth-page-skin > .auth-panel, .password-container.auth-page-skin > .auth-panel, .reset-container.auth-page-skin > .auth-panel {
  grid-column: 2;
  justify-self: end;
  width: min(100%, 500px);
}
.reset-container.auth-page-skin > .language-switcher { position: absolute; z-index: 3; top: 18px; inset-inline-start: 24px; }
@media (max-width: 767px) {
  .otp-container.auth-page-skin, .verify-container.auth-page-skin, .password-container.auth-page-skin, .reset-container.auth-page-skin {
    position: relative;
    display: block;
    min-height: calc(100svh - 56px);
    padding: 34px 14px 40px;
  }
  .otp-container.auth-page-skin::before, .verify-container.auth-page-skin::before, .password-container.auth-page-skin::before, .reset-container.auth-page-skin::before {
    inset: -8% -18%;
    background-size: min(560px, 125vw) auto;
    opacity: .34;
  }
  .otp-container.auth-page-skin > .auth-panel, .verify-container.auth-page-skin > .auth-panel, .password-container.auth-page-skin > .auth-panel, .reset-container.auth-page-skin > .auth-panel {
    position: relative;
    z-index: 2;
    width: min(100%, 440px);
    margin: 0 auto;
  }
}

/* RegisterCompanyPhone has an extra inner container around its panel. */
.auth-page-skin > .login-container {
  display: grid;
  grid-template-columns: minmax(0, 1.08fr) minmax(320px, .92fr);
  align-items: center;
  gap: clamp(24px, 4vw, 72px);
  min-height: calc(100svh - 64px);
  padding: 0;
  background: transparent;
}
.auth-page-skin > .login-container > .login-card {
  grid-column: 2;
  justify-self: end;
  width: min(100%, 500px);
  position: relative;
  z-index: 2;
  background: var(--auth-surface);
  border: 1px solid var(--auth-border);
  border-radius: 22px;
  color: var(--auth-text);
  box-shadow: 0 24px 70px rgba(0,0,0,.28);
  backdrop-filter: blur(14px);
}
@media (max-width: 767px) {
  .auth-page-skin > .login-container { display: block; min-height: calc(100svh - 56px); }
  .auth-page-skin > .login-container > .login-card { width: min(100%, 440px); margin: 0 auto; }
}


/* Keep auth panels translucent like Register so the visual fades through them. */
.auth-page-skin .auth-panel,
.auth-page-skin .login-card,
.register-page .form-card {
  background: transparent !important;
  backdrop-filter: blur(4px);
}

/* Center the visual directly behind the panel on phone widths. */
@media (max-width: 767px) {
  .auth-page-skin::before {
    inset: 0;
    background-position: center center;
    background-size: min(112vw, 440px) auto;
    opacity: .5;
  }

  .auth-page-skin .auth-panel,
  .auth-page-skin .login-card,
  .register-page .form-card {
    position: relative;
    z-index: 4;
    background: transparent !important;
    box-shadow: 0 18px 44px rgba(0, 0, 0, .18);
  }
}

/* Login fields must stay dark, including browser autofill. */
.register-page .form-card .form-input,
.login-page .form-card .form-input,
.login-page input.form-input {
  background: #05181f !important;
  background-color: #05181f !important;
  color: #f1f7f5 !important;
}

.register-page .form-card .form-input:-webkit-autofill,
.login-page .form-card .form-input:-webkit-autofill,
.login-page input.form-input:-webkit-autofill,
.login-page .form-input:-webkit-autofill:hover,
.login-page .form-input:-webkit-autofill:focus {
  -webkit-text-fill-color: #f1f7f5 !important;
  -webkit-box-shadow: 0 0 0 1000px #05181f inset !important;
  box-shadow: 0 0 0 1000px #05181f inset !important;
  background-color: #05181f !important;
  caret-color: #f1f7f5;
}
</style>
